import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const DeploymentDebug = () => {
  const [debugInfo, setDebugInfo] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    const gatherDebugInfo = async () => {
      const info = {
        // Environment variables
        supabaseUrl: process.env.REACT_APP_SUPABASE_URL,
        supabaseKey: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Present' : 'Missing',
        keyLength: process.env.REACT_APP_SUPABASE_ANON_KEY?.length || 0,
        
        // Browser info
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        
        // Supabase client info
        supabaseClient: supabase ? 'Initialized' : 'Not initialized',
      };

      setDebugInfo(info);

      // Test Supabase connection
      try {
        const { data, error } = await supabase.auth.getSession();
        setTestResults(prev => ({
          ...prev,
          sessionTest: error ? `Error: ${error.message}` : 'Success',
          hasSession: data?.session ? 'Yes' : 'No'
        }));
      } catch (err) {
        setTestResults(prev => ({
          ...prev,
          sessionTest: `Exception: ${err.message}`
        }));
      }

      // Test database connection
      try {
        const { error } = await supabase.from('leads').select('count').limit(1);
        setTestResults(prev => ({
          ...prev,
          databaseTest: error ? `Error: ${error.message}` : 'Success'
        }));
      } catch (err) {
        setTestResults(prev => ({
          ...prev,
          databaseTest: `Exception: ${err.message}`
        }));
      }
    };

    gatherDebugInfo();
  }, []);

  const testLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: 'test@example.com',
        password: 'testpassword'
      });
      
      setTestResults(prev => ({
        ...prev,
        loginTest: error ? `Error: ${error.message}` : 'Success'
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        loginTest: `Exception: ${err.message}`
      }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Deployment Debug Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Environment & Configuration</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Connection Tests</h3>
          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded">
              <strong>Session Test:</strong> {testResults.sessionTest || 'Not tested'}
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <strong>Database Test:</strong> {testResults.databaseTest || 'Not tested'}
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <strong>Login Test:</strong> {testResults.loginTest || 'Not tested'}
            </div>
            <button
              onClick={testLogin}
              className="btn-primary text-sm"
            >
              Test Login
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Common Issues & Solutions</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <ul className="text-sm text-gray-700 space-y-2">
            <li><strong>Environment Variables:</strong> Make sure REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY are set in your deployment platform</li>
            <li><strong>CORS Settings:</strong> Add your deployed domain to Supabase Auth settings → URL Configuration</li>
            <li><strong>Site URL:</strong> Update Supabase Auth settings → Site URL to match your deployed domain</li>
            <li><strong>Redirect URLs:</strong> Add your deployed domain to Supabase Auth settings → Redirect URLs</li>
          </ul>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Self-Hosted Supabase Specific Issues</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <ul className="text-sm text-gray-700 space-y-2">
            <li><strong>SSL Certificates:</strong> Ensure your self-hosted Supabase has valid SSL certificates</li>
            <li><strong>Domain Access:</strong> Make sure your deployed app can reach your self-hosted Supabase domain</li>
            <li><strong>Firewall Rules:</strong> Check if your self-hosted Supabase allows connections from your deployment platform</li>
            <li><strong>Auth Configuration:</strong> Verify JWT settings and auth configuration in your self-hosted instance</li>
            <li><strong>Database Connection:</strong> Ensure your self-hosted Supabase database is accessible from external connections</li>
            <li><strong>Environment Variables:</strong> Double-check that your self-hosted Supabase URL and keys are correctly set</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DeploymentDebug;
